import { VdpLayout, VdpTemplate } from '../types';

export interface BoxDimensions {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PdfBoxesResult {
  trimBox: BoxDimensions;
  mediaBox: BoxDimensions;
  bleedBox: BoxDimensions;
  cropBox: BoxDimensions;
  artBox: BoxDimensions;
}

export const STANDARD_BOX_SIZES: Record<string, { name: string; width: number; height: number }> = {
  'A4': { name: 'A4 (210 × 297 mm)', width: 595.28, height: 841.89 },
  'A3': { name: 'A3 (297 × 420 mm)', width: 841.89, height: 1190.55 },
  'A5': { name: 'A5 (148 × 210 mm)', width: 419.53, height: 595.28 },
  'SRA3': { name: 'SRA3 (320 × 450 mm)', width: 907.09, height: 1275.59 },
  'SRA4': { name: 'SRA4 (225 × 320 mm)', width: 637.80, height: 907.09 },
  'letter': { name: 'US Letter (8.5 × 11 in)', width: 612.00, height: 792.00 },
  'legal': { name: 'US Legal (8.5 × 14 in)', width: 612.00, height: 1008.00 },
  'tabloid': { name: 'US Tabloid (11 × 17 in)', width: 792.00, height: 1224.00 },
  '12x18': { name: 'Digital 12 × 18 in', width: 864.00, height: 1296.00 },
  '13x19': { name: 'Super B 13 × 19 in', width: 936.00, height: 1368.00 },
};

/**
 * Resolves base sheet dimensions from layout
 */
export function getSheetDimensions(layout?: VdpLayout): { width: number; height: number } {
  if (!layout) return { width: 595.28, height: 841.89 };

  let width = 595.28;
  let height = 841.89;

  if (layout.pageSize === 'custom' && layout.pageWidth && layout.pageHeight) {
    width = layout.pageWidth;
    height = layout.pageHeight;
  } else if (STANDARD_BOX_SIZES[layout.pageSize]) {
    width = STANDARD_BOX_SIZES[layout.pageSize].width;
    height = STANDARD_BOX_SIZES[layout.pageSize].height;
  }

  if (layout.pageOrientation === 'landscape' && width < height) {
    const temp = width;
    width = height;
    height = temp;
  } else if (layout.pageOrientation === 'portrait' && width > height) {
    const temp = width;
    width = height;
    height = temp;
  }

  return { width, height };
}

/**
 * Calculates effective Trim Box and Media Box rectangles in sheet coordinate space (points)
 */
export function calculatePdfBoxes(
  layout?: VdpLayout,
  template?: VdpTemplate
): PdfBoxesResult {
  const sheet = getSheetDimensions(layout);
  const sheetW = sheet.width;
  const sheetH = sheet.height;

  // 1. Determine Trim Box width and height
  let trimW = layout?.trimBoxWidth || sheetW;
  let trimH = layout?.trimBoxHeight || sheetH;

  const orientation = layout?.trimBoxOrientation || (trimW > trimH ? 'landscape' : 'portrait');
  if (orientation === 'landscape' && trimW < trimH) {
    const t = trimW;
    trimW = trimH;
    trimH = t;
  } else if (orientation === 'portrait' && trimW > trimH) {
    const t = trimW;
    trimW = trimH;
    trimH = t;
  }

  // 2. Determine Media Box Margins around Trim Box (Top, Bottom, Left, Right in points)
  const mTop = layout?.mediaBoxMarginTop !== undefined ? Number(layout.mediaBoxMarginTop) : 14.17;
  const mBottom = layout?.mediaBoxMarginBottom !== undefined ? Number(layout.mediaBoxMarginBottom) : 14.17;
  const mLeft = layout?.mediaBoxMarginLeft !== undefined ? Number(layout.mediaBoxMarginLeft) : 14.17;
  const mRight = layout?.mediaBoxMarginRight !== undefined ? Number(layout.mediaBoxMarginRight) : 14.17;

  // Media Box covers TrimBox + Margins
  const mediaW = trimW + mLeft + mRight;
  const mediaH = trimH + mTop + mBottom;

  // TrimBox starts at (mLeft, mBottom) relative to MediaBox origin (0, 0)
  const trimBox: BoxDimensions = {
    x: mLeft,
    y: mBottom,
    width: trimW,
    height: trimH
  };

  const mediaBox: BoxDimensions = {
    x: 0,
    y: 0,
    width: mediaW,
    height: mediaH
  };

  const bleed = Number(layout?.bleed || 0);
  const bleedBox: BoxDimensions = {
    x: Math.max(0, trimBox.x - bleed),
    y: Math.max(0, trimBox.y - bleed),
    width: trimBox.width + bleed * 2,
    height: trimBox.height + bleed * 2
  };

  const cropBox: BoxDimensions = { ...mediaBox };
  const artBox: BoxDimensions = { ...trimBox };

  return {
    trimBox,
    mediaBox,
    bleedBox,
    cropBox,
    artBox
  };
}

/**
 * Computes grid bounding box for "Fit to Content / Grid"
 */
export function getContentGridBoundingBox(layout?: VdpLayout, template?: VdpTemplate): { width: number; height: number } {
  const rows = layout?.rows || 1;
  const cols = layout?.cols || 1;
  const stickerW = template?.width || 250;
  const stickerH = template?.height || 150;
  const gapX = layout?.gapX || 0;
  const gapY = layout?.gapY || 0;
  const bleed = layout?.bleed || 0;

  const totalW = cols * stickerW + (cols - 1) * gapX + bleed * 2;
  const totalH = rows * stickerH + (rows - 1) * gapY + bleed * 2;

  return {
    width: Math.round(totalW * 100) / 100,
    height: Math.round(totalH * 100) / 100
  };
}
